import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  SYMPTOM,
  DIAGNOSIS,
  TWIST,
  REALBUG,
  CODE,
  PAYOFF,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK : "Two weeks → one line" */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const one = interpolate(frame, [44, 78], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="a debugging war story" color={COLORS.amber} />
          <Big>
            <span style={{ color: COLORS.dim }}>2 weeks debugging.</span>
            <br />
            The fix was{" "}
            <span style={{ color: COLORS.green, opacity: 0.4 + one * 0.6 }}>
              ONE LINE
            </span>
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="amber" />
    </AbsoluteFill>
  );
};

/* 02 — SYMPTOM : sync dies at ~20%, silent */
export const Scene02Symptom: React.FC = () => {
  const frame = useCurrentFrame();
  // progress bar fills to ~20%, then freezes
  const pct = interpolate(frame, [10, 120], [0, 20], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  // a blinking "freeze" indicator after it stalls
  const blink = frame > 130 ? (Math.floor(frame / 14) % 2 === 0 ? 1 : 0.2) : 0;
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.symptom.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the symptom · github sync" color={COLORS.red} />
          <Panel accent="red" title="sync progress" width={1040}>
            {/* progress track */}
            <div
              style={{
                height: 30,
                background: COLORS.bg2,
                borderRadius: 6,
                overflow: "hidden",
                border: `1px solid ${COLORS.line}`,
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${pct}%`,
                  background: COLORS.red,
                  boxShadow: `0 0 22px -4px ${COLORS.red}`,
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginTop: 16,
                fontFamily: FONTS.mono,
                fontSize: 22,
                color: COLORS.dim,
              }}
            >
              <span>chunking 670+ files…</span>
              <span style={{ color: COLORS.red, fontSize: 30 }}>
                {pct.toFixed(0)}%
              </span>
            </div>
            <div
              style={{
                fontFamily: FONTS.mono,
                fontSize: 20,
                color: COLORS.red,
                marginTop: 14,
                opacity: blink,
              }}
            >
              ▍process terminated · no error · no stack trace
            </div>
          </Panel>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={SYMPTOM} accent="red" />
    </AbsoluteFill>
  );
};

/* 03 — DIAGNOSIS : "must be OOM" → upgrade Railway 1GB → 8GB, no change */
export const Scene03Diagnosis: React.FC = () => {
  const frame = useCurrentFrame();
  const stamp = interpolate(frame, [150, 180], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.diagnosis.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the obvious diagnosis" color={COLORS.amber} />
          <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
            <Panel accent="amber" title="hypothesis" width={460}>
              <div
                style={{
                  fontFamily: FONTS.display,
                  fontWeight: 700,
                  fontSize: 70,
                  color: COLORS.amber,
                }}
              >
                OOM
              </div>
              <div
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 24,
                  color: COLORS.dim,
                  marginTop: 8,
                  lineHeight: 1.4,
                }}
              >
                out of memory — the box is small
              </div>
            </Panel>
            <Panel accent="amber" title="the fix I tried" width={620}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 18,
                  fontFamily: FONTS.display,
                  fontWeight: 700,
                  fontSize: 52,
                  color: COLORS.text,
                }}
              >
                <span style={{ color: COLORS.dim }}>1 GB</span>
                <span style={{ color: COLORS.amber, fontSize: 40 }}>→</span>
                <span>8 GB</span>
              </div>
              <div
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 19,
                  color: COLORS.dim,
                  marginTop: 10,
                }}
              >
                upgraded Railway to the Hobby plan
              </div>
              <div
                style={{
                  marginTop: 18,
                  fontFamily: FONTS.mono,
                  fontSize: 24,
                  letterSpacing: "0.06em",
                  color: COLORS.red,
                  opacity: stamp,
                  border: `1px solid ${COLORS.red}`,
                  borderRadius: 6,
                  padding: "8px 14px",
                  display: "inline-block",
                }}
              >
                STILL CRASHED AT 20%
              </div>
            </Panel>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={DIAGNOSIS} accent="amber" />
    </AbsoluteFill>
  );
};

/* 04 — TWIST : the sync never ran on Railway */
const TOPO: PipeNode[] = [
  { label: "trigger", value: "POST /sync", accent: "cyan" },
  { label: "runs on", value: "Dokploy VPS", sub: "in-process setTimeout(0)", accent: "green" },
  { label: "NOT on", value: "Railway", sub: "docling OCR only", accent: "red" },
];
export const Scene04Twist: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.twist.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
      <Kicker text="the plot twist · where it runs" color={COLORS.violet} />
      <div style={{ width: 1560 }}>
        <PipelineGraph nodes={TOPO} startFrame={8} stepFrames={26} nodeWidth={300} />
      </div>
      <div
        style={{
          fontFamily: FONTS.body,
          fontSize: 28,
          color: COLORS.dim,
          marginTop: 40,
          textAlign: "center",
          maxWidth: 1300,
        }}
      >
        I tuned memory on a server the sync code never touched.
      </div>
    </AbsoluteFill>
    <Caption lines={TWIST} accent="violet" />
  </AbsoluteFill>
);

/* 05 — REALBUG : the mutual-recursion cycle */
export const Scene05RealBug: React.FC = () => {
  const frame = useCurrentFrame();
  // the cycle arrows pulse to imply "forever"
  const pulse = 0.5 + 0.5 * Math.sin(frame / 7);
  const reveal = (f: number) =>
    interpolate(frame, [f, f + 18], [0, 1], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.realbug.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the real bug · stack overflow" color={COLORS.magenta} />
          <div style={{ display: "flex", alignItems: "center", gap: 30 }}>
            <div style={{ opacity: reveal(8) }}>
              <Panel accent="cyan" title="chunkMarkdownStructured()" width={430}>
                <div
                  style={{
                    fontFamily: FONTS.mono,
                    fontSize: 20,
                    lineHeight: 1.7,
                    color: COLORS.text,
                  }}
                >
                  section &gt; 4096 chars?
                  <br />
                  <span style={{ color: COLORS.dim }}>→ ask general to split</span>
                </div>
              </Panel>
            </div>
            {/* the cycle */}
            <div style={{ textAlign: "center", opacity: reveal(34) }}>
              <div
                style={{
                  fontFamily: FONTS.display,
                  fontSize: 56,
                  color: COLORS.magenta,
                  opacity: 0.4 + pulse * 0.6,
                  lineHeight: 1,
                }}
              >
                ⇄
              </div>
              <div
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 16,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: COLORS.magenta,
                  marginTop: 6,
                }}
              >
                forever
              </div>
            </div>
            <div style={{ opacity: reveal(60) }}>
              <Panel accent="violet" title="chunkGeneralStructured()" width={430}>
                <div
                  style={{
                    fontFamily: FONTS.mono,
                    fontSize: 20,
                    lineHeight: 1.7,
                    color: COLORS.text,
                  }}
                >
                  has headings?
                  <br />
                  <span style={{ color: COLORS.dim }}>→ hand back to markdown</span>
                </div>
              </Panel>
            </div>
          </div>
          <div
            style={{
              opacity: reveal(96),
              marginTop: 34,
              fontFamily: FONTS.mono,
              fontSize: 22,
              color: COLORS.red,
              border: `1px solid ${COLORS.red}55`,
              borderRadius: 8,
              padding: "10px 18px",
            }}
          >
            same section, never shrinks → RangeError: stack overflow → silent exit
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={REALBUG} accent="magenta" />
    </AbsoluteFill>
  );
};

/* 06 — CODE : the one-line fix */
const FIX = `// src/lib/rag/adaptive-chunker.ts  ·  chunkMarkdownStructured()
if (section.length > MAX_CHUNK_CHARS) {
  // BEFORE — delegated to the structured chunker:
  //   const sub = await chunkGeneralStructured(section, filePath)
  //   ↳ sees the headings, calls markdown again → infinite cycle

  // AFTER — call the plain splitter directly. No heading check,
  // no delegation, no loop. The cycle is broken.
  const plainChunks = await chunkGeneral(section)
  result.push(...plainChunks.map(toDocSectionChunk))
}`;
export const Scene06Code: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.code.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}>
      <CodePanel
        code={FIX}
        language="tsx"
        filename="adaptive-chunker.ts"
        highlightLines={[9, 10]}
        revealFrames={260}
        fontSize={23}
        width={1280}
      />
    </AbsoluteFill>
    <Caption lines={CODE} accent="green" />
  </AbsoluteFill>
);

/* 07 — PAYOFF : 2,110 chunks · flat memory · 0 errors */
export const Scene07Payoff: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [110, 140], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const c2 = interpolate(frame, [200, 230], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.payoff.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="sync · after the one-line fix" color={COLORS.green} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 16 }}>
          <MetricCounter
            value={2110}
            from={0}
            startFrame={10}
            durationFrames={60}
            accent="green"
            fontSize={150}
            format={(n) => Math.round(n).toLocaleString()}
          />
          <span
            style={{
              fontFamily: FONTS.display,
              fontWeight: 700,
              fontSize: 54,
              color: COLORS.dim,
            }}
          >
            chunks
          </span>
        </div>
        <div style={{ display: "flex", gap: 30, marginTop: 38 }}>
          <div style={{ opacity: c1 }}>
            <Callout label="memory profile" accent="cyan" width={360}>
              heap <b style={{ color: COLORS.text }}>flat</b> across 670+ files
            </Callout>
          </div>
          <div style={{ opacity: c2 }}>
            <Callout label="errors" accent="green" width={360}>
              <b style={{ color: COLORS.text }}>0</b> · sync completes every time
            </Callout>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={PAYOFF} accent="green" />
    </AbsoluteFill>
  );
};

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={66}>
          The hardest bugs
          <br />
          disguise themselves as the obvious one.
          <br />
          <span style={{ color: COLORS.cyan }}>Ask where the code actually runs.</span>
        </Big>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            letterSpacing: "0.24em",
            color: COLORS.dim,
            marginTop: 44,
          }}
        >
          EAKC · debugging in public
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
